import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  List,
  Modal,
  Input,
  message,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Alert,
  Spin,
  Typography
} from 'antd';
import {
  EditOutlined,
  ReloadOutlined,
  FileTextOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
  SettingOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import GameConfig from './GameConfigs/GameConfig';

const { TextArea } = Input;
const { Text } = Typography;

interface ConfigFile {
  name: string;
  path: string;
  size: number;
  modified: string;
  type: string;
}

interface ConfigManagementProps {
  serverId: string;
  gameType: string;
  serverInfo?: any;
  onTabChange?: (tabKey: string) => void;
}

const ConfigManagement: React.FC<ConfigManagementProps> = ({ serverId, gameType, serverInfo, onTabChange }) => {
  const [configFiles, setConfigFiles] = useState<ConfigFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentFile, setCurrentFile] = useState<ConfigFile | null>(null);
  const [fileContent, setFileContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConfigFiles();
  }, [serverId]);

  const loadConfigFiles = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/config/servers/${serverId}/files`);
      setConfigFiles(response.data);
      setError(null);
    } catch (error: any) {
      console.error('加载配置文件失败:', error);
      setError(error.response?.data?.message || '加载配置文件失败');
    } finally {
      setLoading(false);
    }
  };

  const loadFileContent = async (file: ConfigFile) => {
    try {
      setLoading(true);
      const encodedPath = encodeURIComponent(file.path);
      const response = await api.get(`/config/servers/${serverId}/files/${encodedPath}`);
      setFileContent(response.data.content);
      setOriginalContent(response.data.content);
      setCurrentFile(file);
      setEditModalVisible(true);
      setError(null);
    } catch (error: any) {
      console.error('加载文件内容失败:', error);
      message.error(error.response?.data?.message || '加载文件内容失败');
    } finally {
      setLoading(false);
    }
  };

  const saveFileContent = async () => {
    if (!currentFile) return;

    try {
      setSaving(true);
      const encodedPath = encodeURIComponent(currentFile.path);
      await api.put(`/config/servers/${serverId}/files/${encodedPath}`, {
        content: fileContent
      });
      
      message.success('配置文件保存成功');
      setEditModalVisible(false);
      loadConfigFiles(); // 重新加载文件列表以更新修改时间
    } catch (error: any) {
      console.error('保存文件失败:', error);
      const errorMessage = error.response?.data?.message || '保存文件失败';
      if (error.response?.data?.errors) {
        message.error(`${errorMessage}: ${error.response.data.errors.join(', ')}`);
      } else {
        message.error(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const resetFileContent = async () => {
    if (!currentFile) return;

    try {
      setSaving(true);
      const encodedPath = encodeURIComponent(currentFile.path);
      await api.post(`/config/servers/${serverId}/files/${encodedPath}/reset`);
      
      message.success('配置文件已重置为默认值');
      setEditModalVisible(false);
      loadConfigFiles();
    } catch (error: any) {
      console.error('重置文件失败:', error);
      message.error(error.response?.data?.message || '重置文件失败');
    } finally {
      setSaving(false);
    }
  };

  const getFileTypeTag = (type: string) => {
    const typeMap = {
      properties: { color: 'blue', text: 'Properties' },
      yaml: { color: 'green', text: 'YAML' },
      json: { color: 'orange', text: 'JSON' },
      config: { color: 'purple', text: 'Config' },
      ini: { color: 'cyan', text: 'INI' },
      text: { color: 'default', text: 'Text' }
    };
    
    const typeInfo = typeMap[type as keyof typeof typeMap] || { color: 'default', text: type };
    return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  const hasChanges = fileContent !== originalContent;

  if (loading && configFiles.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '32px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>加载配置文件中...</div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert
          message="加载失败"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={loadConfigFiles}>
              重试
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <div>
      <Card
        title={
          <Space>
            <SettingOutlined />
            <span>服务器配置管理</span>
            <Tag color="blue">{gameType}</Tag>
          </Space>
        }
      >
        <GameConfig 
          serverId={serverId} 
          gameType={gameType} 
          serverInfo={serverInfo}
          onTabChange={onTabChange}
        />
      </Card>

      {/* 编辑配置文件模态框 */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>编辑配置文件</span>
            {currentFile && (
              <>
                <Text type="secondary">-</Text>
                <Text code>{currentFile.name}</Text>
              </>
            )}
          </Space>
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width="80%"
        style={{ top: 20 }}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            取消
          </Button>,
          <Popconfirm
            key="reset"
            title="确定要重置配置文件吗？"
            description="这将把文件内容重置为默认值，当前修改将丢失。"
            onConfirm={resetFileContent}
            okText="确定"
            cancelText="取消"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
          >
            <Button danger loading={saving}>
              重置为默认
            </Button>
          </Popconfirm>,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            onClick={saveFileContent}
            loading={saving}
            disabled={!hasChanges}
          >
            保存更改
          </Button>
        ]}
      >
        {hasChanges && (
          <Alert
            message="检测到未保存的更改"
            description="您对配置文件进行了修改，请记得保存更改。"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        <TextArea
          value={fileContent}
          onChange={(e) => setFileContent(e.target.value)}
          rows={20}
          style={{ fontFamily: 'monospace' }}
          placeholder="配置文件内容..."
        />
        
        <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
          <Text type="secondary">
            提示：修改配置文件后需要重启服务器才能生效
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default ConfigManagement;
